import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/workspace/DashboardPage';
import DocumentsPage from './pages/workspace/DocumentsPage';
import IntegrationsPage from './pages/workspace/IntegrationsPage';
import ProjectsPage from './pages/workspace/ProjectsPage';
import TeamsPage from './pages/workspace/TeamsPage';
import ContentCreationPage from './pages/workspace/ContentCreationPage';
import UseCasesPage from './pages/workspace/UseCasesPage';
import AssistantPage from './pages/workspace/AssistantPage';
import ConversationsPage from './pages/workspace/ConversationsPage';
import ProjetsPage from './pages/workspace/ProjetsPage';
import ReunionsPage from './pages/workspace/ReunionsPage';
import NouvelleConversationPage from './pages/workspace/NouvelleConversationPage';
import CoworkPage from './pages/workspace/CoworkPage';
import MemoirePage from './pages/workspace/MemoirePage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RedactionPage from './pages/workspace/RedactionPage';
import FreeAssistedPage from './pages/workspace/FreeAssistedPage';
import DocumentsLibrePage from './pages/workspace/DocumentsLibrePage';
import DocumentViewerPage from './pages/workspace/DocumentViewerPage';
import AIVisualGeneratorPage from './pages/workspace/AIVisualGeneratorPage';
import AnalyticsPage from './pages/workspace/AnalyticsPage';
import SettingsPage from './pages/workspace/SettingsPage';
import AdminPage from './pages/workspace/AdminPage';
import DocumentManagementPage from './pages/DocumentManagementPage';
import StrategicWritingPage from './pages/StrategicWritingPage';
import EvaluationTrackingPage from './pages/EvaluationTrackingPage';
import CommunicationCreationPage from './pages/CommunicationCreationPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import GardeFouRendu from './components/GardeFouRendu';
import { ACCUEIL, ESPACE_ADMIN, URL_ADMIN, URL_OMNIA } from './utils/espace';
import BandeauInstitutionnel from './components/layout/BandeauInstitutionnel';

/**
 * Côté OmnIA, les adresses de l'administration mènent au site qui la porte.
 * Les favoris et les liens d'avant la séparation restent valables.
 */
const VersAdministration: React.FC<{ chemin: string }> = ({ chemin }) => {
  React.useEffect(() => {
    if (URL_ADMIN) window.location.replace(`${URL_ADMIN}${chemin}`);
  }, [chemin]);
  return URL_ADMIN ? null : <Navigate to="/workspace/conversations" replace />;
};

/** Le site d'administration refuse poliment un compte qui n'en est pas. */
const ReserveAuxAdministrateurs: React.FC = () => (
  <div className="flex min-h-screen flex-col bg-eg-fond">
    <div className="flex flex-1 items-center justify-center px-4">
      <div className="max-w-[420px] text-center">
        <img src="/logo-omnia.svg" alt="OmnIA" className="mx-auto mb-6 h-10 w-auto" />
        <h1 className="text-[22px] font-semibold tracking-tight text-eg-encre">
          Réservé aux administrateurs du programme
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-eg-gris">
          Ce site sert à administrer la plateforme : mémoire du PEV, comptes, paramètres.
          Votre compte n'y a pas accès.
        </p>
        {URL_OMNIA && (
          <a
            href={URL_OMNIA}
            className="mt-6 inline-flex rounded-full bg-eg-ombre px-4 py-2 text-[13px] font-medium text-white hover:bg-black"
          >
            Aller sur OmnIA
          </a>
        )}
      </div>
    </div>
    <BandeauInstitutionnel />
  </div>
);

/** Le site d'administration du PEV : la mémoire, les comptes, les paramètres. */
const RoutesAdministration: React.FC = () => {
  const admin = (element: React.ReactNode) => (
    <ProtectedRoute allowedRoles={['admin']} redirectTo="/reserve">{element}</ProtectedRoute>
  );
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reserve" element={<ReserveAuxAdministrateurs />} />
      <Route path="/" element={<Navigate to={ACCUEIL} replace />} />
      <Route path="/workspace" element={<Navigate to={ACCUEIL} replace />} />
      <Route path="/workspace/memoire/parametres" element={admin(<SettingsPage />)} />
      <Route path="/workspace/memoire/comptes" element={admin(<AdminPage />)} />
      <Route path="/workspace/memoire" element={admin(<MemoirePage />)} />
      <Route path="/workspace/memoire/:onglet" element={admin(<MemoirePage />)} />
      <Route path="/workspace/profile" element={admin(<SettingsPage />)} />
      <Route path="/workspace/settings" element={<Navigate to="/workspace/memoire/parametres" replace />} />
      <Route path="/workspace/admin" element={<Navigate to="/workspace/memoire/comptes" replace />} />
      <Route path="*" element={<Navigate to={ACCUEIL} replace />} />
    </Routes>
  );
};

function App() {
  if (ESPACE_ADMIN) {
    return (
      <Router>
        <div className="App">
          <GardeFouRendu>
            <RoutesAdministration />
          </GardeFouRendu>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <div className="App">
        <GardeFouRendu>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
          
          {/* Routes protégées du dashboard */}
          <Route path="/workspace" element={<Navigate to="/workspace/conversations" replace />} />
          <Route path="/workspace/redaction" element={<ProtectedRoute><RedactionPage /></ProtectedRoute>} />
          <Route path="/workspace/redaction-libre" element={<ProtectedRoute><FreeAssistedPage /></ProtectedRoute>} />
          <Route path="/workspace/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
          <Route path="/workspace/documents/:documentId" element={<ProtectedRoute><DocumentViewerPage /></ProtectedRoute>} />
          <Route path="/workspace/documents-libre" element={<ProtectedRoute><DocumentsLibrePage /></ProtectedRoute>} />
          <Route path="/workspace/integrations" element={
            <ProtectedRoute>
              <IntegrationsPage />
            </ProtectedRoute>
          } />
          <Route path="/workspace/evaluation" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/workspace/search" element={
            <ProtectedRoute>
              <div className="p-8 text-center">Page de recherche avancée</div>
            </ProtectedRoute>
          } />
          <Route path="/workspace/projects" element={
            <ProtectedRoute requiredCapabilities={['projects.view']}>
              <ProjectsPage />
            </ProtectedRoute>
          } />
          <Route path="/workspace/teams" element={
            <ProtectedRoute requiredCapabilities={['teams.view']}>
              <TeamsPage />
            </ProtectedRoute>
          } />
          <Route path="/workspace/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/workspace/content" element={<ProtectedRoute><ContentCreationPage /></ProtectedRoute>} />
          <Route path="/workspace/ai-visual-generator" element={<ProtectedRoute><AIVisualGeneratorPage /></ProtectedRoute>} />
          <Route path="/workspace/usecases" element={<ProtectedRoute><UseCasesPage /></ProtectedRoute>} />
          <Route path="/workspace/assistant/:templateId" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
          <Route path="/workspace/conversation/:conversationId" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
          <Route path="/workspace/communication" element={<ProtectedRoute><NouvelleConversationPage communication /></ProtectedRoute>} />
          <Route path="/workspace/nouvelle" element={<Navigate to="/workspace/conversations" replace />} />
          <Route path="/workspace/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
          <Route path="/workspace/cowork" element={<ProtectedRoute><CoworkPage /></ProtectedRoute>} />
          <Route path="/workspace/activites" element={<ProtectedRoute><ProjetsPage /></ProtectedRoute>} />
          <Route path="/workspace/reunions" element={<ProtectedRoute><ReunionsPage /></ProtectedRoute>} />
          <Route path="/workspace/profile" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          {/* L'administration a son propre site. Ses anciennes adresses y
              mènent, pour les favoris et les liens. */}
          <Route path="/workspace/settings" element={<VersAdministration chemin="/workspace/memoire/parametres" />} />
          <Route path="/workspace/admin" element={<VersAdministration chemin="/workspace/memoire/comptes" />} />
          <Route path="/workspace/memoire/*" element={<VersAdministration chemin="/workspace/memoire" />} />
          <Route path="/workspace/memoire" element={<VersAdministration chemin="/workspace/memoire" />} />
          
          {/* Routes publiques pour les modules */}
          <Route path="/document-management" element={<DocumentManagementPage />} />
          <Route path="/strategic-writing" element={<StrategicWritingPage />} />
          <Route path="/evaluation-tracking" element={<EvaluationTrackingPage />} />
          <Route path="/communication-creation" element={<CommunicationCreationPage />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </GardeFouRendu>
      </div>
    </Router>
  );
}

export default App;
